import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Check, Zap, TrendingUp, Crown, ArrowRight, 
  CreditCard, Clock, Shield, Sparkles, Loader2, ArrowLeft
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingTier = ({ 
  name, 
  price, 
  perQuote, 
  quotes, 
  features, 
  popular, 
  icon: Icon,
  onSelect,
  buttonText = "Get Started",
  isLoading
}) => (
  <Card className={`relative bg-card border-2 transition-all duration-300 hover:scale-105 ${
    popular ? 'border-primary shadow-lg shadow-primary/20' : 'border-white/10 hover:border-white/20'
  }`} data-testid={`pricing-tier-${name.toLowerCase().replace(/\s+/g, '-')}`}>
    {popular && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <Badge className="bg-primary text-primary-foreground px-3 py-1">
          Most Popular
        </Badge>
      </div>
    )}
    
    <CardHeader className="text-center pb-2 pt-8">
      <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
        popular ? 'bg-primary/20' : 'bg-secondary'
      }`}>
        <Icon className={`w-6 h-6 ${popular ? 'text-primary' : 'text-muted-foreground'}`} />
      </div>
      <CardTitle className="text-xl">{name}</CardTitle>
      {quotes && (
        <p className="text-sm text-muted-foreground">{quotes} quotes/month</p>
      )}
    </CardHeader>
    
    <CardContent className="text-center">
      <div className="mb-6">
        <span className="text-4xl font-bold">£{price}</span>
        {perQuote && (
          <span className="text-muted-foreground ml-1">
            {quotes ? '/month' : '/quote'}
          </span>
        )}
        {quotes && (
          <p className="text-sm text-primary mt-1">
            £{perQuote} per quote
          </p>
        )}
      </div>
      
      <ul className="space-y-3 text-left mb-6">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm">
            <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span className="text-muted-foreground">{feature}</span>
          </li>
        ))}
      </ul>
      
      <Button 
        onClick={onSelect}
        disabled={isLoading}
        className={`w-full ${
          popular 
            ? 'bg-primary hover:bg-primary/90 text-primary-foreground' 
            : 'bg-secondary hover:bg-secondary/80'
        }`}
        data-testid={`select-plan-${name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            {buttonText}
            <ArrowRight className="w-4 h-4 ml-2" />
          </>
        )}
      </Button>
    </CardContent>
  </Card>
);

const Pricing = ({ user }) => {
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  
  const handleSelectPlan = async (planId) => {
    if (!user) {
      // Not logged in - redirect to landing to sign in first
      toast.info("Please sign in to purchase a plan");
      navigate("/");
      return;
    }
    
    setLoadingPlan(planId);
    
    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API}/payments/create-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          plan_id: planId,
          origin_url: originUrl
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create checkout");
      }
      
      const data = await response.json();
      
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.message || "Failed to start checkout. Please try again.");
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Back button */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-6">
        <Button 
          variant="ghost" 
          onClick={() => navigate(user ? "/dashboard" : "/")}
          className="text-muted-foreground hover:text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {user ? "Back to Dashboard" : "Back to Home"}
        </Button>
      </div>
      
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-20 relative">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
              Simple, Transparent Pricing
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Pay for What You Use
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              No hidden fees. No contracts. Start with pay-as-you-go or save more with a subscription.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* PAYG */}
            <PricingTier
              name="Pay As You Go"
              price="9"
              perQuote={true}
              icon={Zap}
              features={[
                "No commitment",
                "Pay per quote generated",
                "Full AI-powered quotes",
                "Live Screwfix pricing",
                "PDF export & sharing",
                "Perfect for occasional use"
              ]}
              onSelect={() => handleSelectPlan('payg')}
              buttonText="Start Free"
              isLoading={loadingPlan === 'payg'}
            />
            
            {/* Starter */}
            <PricingTier
              name="Starter"
              price="59"
              perQuote="2.36"
              quotes={25}
              icon={TrendingUp}
              features={[
                "25 quotes per month",
                "74% savings vs PAYG",
                "All PAYG features",
                "Priority support",
                "Cancel anytime"
              ]}
              onSelect={() => handleSelectPlan('starter')}
              isLoading={loadingPlan === 'starter'}
            />
            
            {/* Professional */}
            <PricingTier
              name="Professional"
              price="99"
              perQuote="1.32"
              quotes={75}
              icon={Crown}
              popular={true}
              features={[
                "75 quotes per month",
                "85% savings vs PAYG",
                "All Starter features",
                "Quote templates",
                "Usage analytics",
                "Best value for busy plumbers"
              ]}
              onSelect={() => handleSelectPlan('professional')}
              isLoading={loadingPlan === 'professional'}
            />
            
            {/* Enterprise */}
            <PricingTier
              name="Business"
              price="150"
              perQuote="1.00"
              quotes={150}
              icon={Sparkles}
              features={[
                "150 quotes per month",
                "89% savings vs PAYG",
                "All Professional features",
                "Multiple users",
                "Custom branding",
                "Dedicated support"
              ]}
              onSelect={() => handleSelectPlan('business')}
              isLoading={loadingPlan === 'business'}
            />
          </div>

          {/* What happens when you run out */}
          <Card className="bg-card/50 border-white/5 mb-16">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                What happens if I use all my credits?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center">A</span>
                    Continue on PAYG
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Automatically switch to £2/quote for the rest of the month. No interruption to your work.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center">B</span>
                    Borrow from Next Month
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Use next month&apos;s credits early. They&apos;ll be deducted when your plan renews.
                  </p>
                </div>
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-sm flex items-center justify-center">C</span>
                    Upgrade Your Plan
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Move to a higher tier mid-month. Pay the difference and get instant access to more credits.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trust Signals */}
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="space-y-2">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold">No Lock-in</h3>
              <p className="text-sm text-muted-foreground">
                Cancel your subscription anytime. No cancellation fees, no hassle.
              </p>
            </div>
            <div className="space-y-2">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <CreditCard className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold">Secure Payments</h3>
              <p className="text-sm text-muted-foreground">
                Powered by Stripe. Your card details are never stored on our servers.
              </p>
            </div>
            <div className="space-y-2">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold">Usage Tracking</h3>
              <p className="text-sm text-muted-foreground">
                We&apos;ll notify you when running low and suggest downgrades if you&apos;re not using your quota.
              </p>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <Card className="bg-card/50 border-white/5">
                <CardContent className="pt-4">
                  <h4 className="font-semibold mb-2">Why no rollover on unused credits?</h4>
                  <p className="text-sm text-muted-foreground">
                    We&apos;ve kept it simple - the big price drop from PAYG (£9) to subscription (as low as £1.00) 
                    already gives you significant savings. No rollover means less admin for everyone, 
                    and we&apos;ll proactively suggest a downgrade if you&apos;re consistently not using your quota.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card/50 border-white/5">
                <CardContent className="pt-4">
                  <h4 className="font-semibold mb-2">When am I charged for PAYG quotes?</h4>
                  <p className="text-sm text-muted-foreground">
                    You fill in all your job details first, then when you click &quot;Generate Quote&quot; 
                    we&apos;ll ask for payment. This way you&apos;re only paying once you&apos;re ready to commit.
                    Card details are saved for quick future payments.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-card/50 border-white/5">
                <CardContent className="pt-4">
                  <h4 className="font-semibold mb-2">Can I switch plans mid-month?</h4>
                  <p className="text-sm text-muted-foreground">
                    Absolutely! Upgrade anytime and pay the prorated difference. 
                    Downgrades take effect from your next billing date.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to streamline your quoting?</h2>
            <p className="text-muted-foreground mb-6">
              Start with pay-as-you-go - no credit card required until your first quote.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90"
                onClick={() => handleSelectPlan('payg')}
                disabled={loadingPlan === 'payg'}
              >
                {loadingPlan === 'payg' ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    Start Free with PAYG
                  </>
                )}
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => navigate('/')}
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
